/**
 * Client-side geolocation for session audit (optional).
 * Uses browser Geolocation API to capture GPS coordinates.
 * Backend handles reverse geocoding. All operations are non-blocking; failures are silent.
 */

const CLIENT_LOCATION_KEY = 'client_location';
const LOCATION_EXPIRY_MS = 60 * 60 * 1000; // 1 hour

/** Value stored when user explicitly denies location permission. */
export const CLIENT_LOCATION_DENIED = '__LOCATION_DENIED__';

interface StoredGeolocation {
  latitude: number;
  longitude: number;
  timestamp: number;
}

interface StoredLocationValue {
  value: string | StoredGeolocation;
  timestamp: number;
}

const B64_PREFIX = 'b64:';

/**
 * Encode location data (coordinates or legacy string) for use in HTTP header.
 */
export function encodeClientLocationForHeader(value: string | { latitude: number; longitude: number }): string {
  try {
    if (typeof value === 'object' && 'latitude' in value && 'longitude' in value) {
      // Send coordinates as "lat,lon" format
      return `${value.latitude},${value.longitude}`;
    }
    // Legacy string format (for backward compatibility)
    return B64_PREFIX + btoa(unescape(encodeURIComponent(value)));
  } catch {
    return '';
  }
}

/**
 * Check if user has explicitly denied location permission.
 */
export function isLocationDenied(): boolean {
  const stored = getStoredClientLocation();
  return stored === CLIENT_LOCATION_DENIED;
}

/**
 * Get stored client location (coordinates or legacy string) if valid (not expired).
 */
export function getStoredClientLocation(): string | { latitude: number; longitude: number } | null {
  try {
    const raw = sessionStorage.getItem(CLIENT_LOCATION_KEY);
    if (!raw) return null;
    const data: StoredLocationValue = JSON.parse(raw);
    if (Date.now() - data.timestamp > LOCATION_EXPIRY_MS) return null;
    return data.value || null;
  } catch {
    return null;
  }
}

/**
 * Get stored client coordinates if available.
 */
export function getStoredClientCoordinates(): { latitude: number; longitude: number } | null {
  const stored = getStoredClientLocation();
  if (stored && typeof stored === 'object' && 'latitude' in stored && 'longitude' in stored) {
    return stored;
  }
  return null;
}

/**
 * Store client location (coordinates or denial string) for use in API requests.
 */
function setStoredClientLocation(value: string | { latitude: number; longitude: number }): void {
  try {
    sessionStorage.setItem(
      CLIENT_LOCATION_KEY,
      JSON.stringify({ value, timestamp: Date.now() })
    );
  } catch {
    // Ignore storage errors
  }
}

/**
 * Request browser geolocation (optional, requires user permission).
 * On success: stores coordinates for use in API requests.
 */
export function requestClientLocation(): void {
  if (!navigator?.geolocation?.getCurrentPosition) return;

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const { latitude, longitude } = pos.coords;
      setStoredClientLocation({ latitude, longitude });
    },
    (err) => {
      // PERMISSION_DENIED (code 1): user explicitly declined - store to send to backend
      if (err?.code === 1) setStoredClientLocation(CLIENT_LOCATION_DENIED);
    },
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
  );
}

export interface LocationResult {
  status: 'granted' | 'denied' | 'unsupported';
  coordinates?: { latitude: number; longitude: number };
}

/**
 * Request browser geolocation and return a Promise with the result.
 * Use this when you need to block until the user grants or denies permission.
 * Returns coordinates on success for use in login/session creation.
 */
export function requestClientLocationAsync(): Promise<LocationResult> {
  return new Promise((resolve) => {
    if (!navigator?.geolocation?.getCurrentPosition) {
      resolve({ status: 'unsupported' });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const coordinates = { latitude, longitude };
        setStoredClientLocation(coordinates);
        resolve({ status: 'granted', coordinates });
      },
      (err) => {
        // PERMISSION_DENIED (code 1): user explicitly declined
        if (err?.code === 1) {
          setStoredClientLocation(CLIENT_LOCATION_DENIED);
          resolve({ status: 'denied' });
        } else {
          // Timeout, position unavailable, etc. - treat as denied for strict policy
          resolve({ status: 'denied' });
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
}
